import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

export interface NodeRunMenuState {
  x: number;
  y: number;
  /** 오른쪽 클릭한 노드 */
  nodeId: string;
  /** 그 노드가 여러 개 선택된 것 중 하나면 선택된 노드 전부, 아니면 그 노드 하나 */
  nodeIds: string[];
}

interface Props {
  menu: NodeRunMenuState;
  onClose: () => void;
}

/**
 * 노드 오른쪽 클릭 메뉴 — 부분 실행(여기부터 / 이 노드만).
 *
 * 실행은 헤더(ModernLayout)가 맡는다: 저장 여부 확인·재배포·상태 폴링이 모두 거기 있어서, 여기서는
 * `openflow:run-scope` 이벤트로 범위만 넘긴다. 두 경로가 따로 실행을 부르면 저장 확인이 빠지는 쪽이 생긴다.
 */
const NodeRunMenu: React.FC<Props> = ({ menu, onClose }) => {
  const { t } = useTranslation();
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const down = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) onClose(); };
    const key = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('mousedown', down, true);
    window.addEventListener('keydown', key);
    window.addEventListener('wheel', onClose, { passive: true });
    window.addEventListener('blur', onClose);
    return () => {
      window.removeEventListener('mousedown', down, true);
      window.removeEventListener('keydown', key);
      window.removeEventListener('wheel', onClose);
      window.removeEventListener('blur', onClose);
    };
  }, [onClose]);

  const run = (mode: 'only' | 'from') => {
    onClose();
    window.dispatchEvent(new CustomEvent('openflow:run-scope', { detail: { mode, nodes: menu.nodeIds } }));
  };
  const many = menu.nodeIds.length > 1;
  // 화면 오른쪽·아래 끝에서 잘리지 않게 안쪽으로 당긴다
  const left = Math.min(menu.x, window.innerWidth - 230);
  const top = Math.min(menu.y, window.innerHeight - 110);

  return (
    <div
      ref={ref}
      role="menu"
      className="fixed z-[70] w-[220px] rounded border border-gray-200 bg-white py-1 text-sm shadow-lg"
      style={{ left, top }}
      onContextMenu={(e) => e.preventDefault()}
    >
      <div className="px-3 pb-1 pt-0.5 text-[0.6875rem] text-gray-400">{many ? t('workflow:runMenu.selected', { n: menu.nodeIds.length }) : t('workflow:runMenu.partial')}</div>
      <button role="menuitem" className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-gray-800 hover:bg-blue-50"
        onClick={() => run('from')} title={t('workflow:runMenu.fromTitle')}>
        <span className="text-green-600">▶</span>{many ? t('workflow:runMenu.fromMany') : t('workflow:runMenu.from')}
      </button>
      <button role="menuitem" className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-gray-800 hover:bg-blue-50"
        onClick={() => run('only')} title={t('workflow:runMenu.onlyTitle')}>
        <span className="text-blue-600">▷</span>{many ? t('workflow:runMenu.onlyMany', { n: menu.nodeIds.length }) : t('workflow:runMenu.only')}
      </button>
    </div>
  );
};

export default NodeRunMenu;
